import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Building2 as FaBuilding, Save as FaSave } from 'lucide-react';
import api from '../../api/axios';
import Loader from '../../components/Loader';
import GradientButton from '../../components/GradientButton';
import { getUserDetails } from '../../actions/userActions';

function EmployerSettings() {
  const dispatch = useDispatch();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const [formData, setFormData] = useState({
    company_name: '',
    nip: '',
    company_address: '',
    company_city: '',
    company_phone: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const res = await api.get('/employer/settings/');
        setFormData({
          company_name: res.data.company_name || '',
          nip: res.data.nip || '',
          company_address: res.data.company_address || '',
          company_city: res.data.company_city || '',
          company_phone: res.data.company_phone || '',
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.put('/employer/settings/', formData);
      setSuccess('Dane firmy zostały zapisane');
      // Odśwież dane usera w Reduxie, żeby np. nagłówki w innych widokach też się zaktualizowały
      dispatch(getUserDetails('profile'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.nip?.[0] || err.response?.data?.detail || 'Nie udało się zapisać danych.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Ustawienia firmy</h1>
          <p className="pub-muted mb-0">
            Zalogowano jako <strong>{userInfo?.email}</strong>
          </p>
        </motion.div>

        {success && <Alert variant="success" className="border-0" style={{ borderRadius: '15px' }}>{success}</Alert>}
        {error && <Alert variant="danger" className="border-0" style={{ borderRadius: '15px' }}>{error}</Alert>}

        <Row>
          <Col xs={12} lg={8}>
            <motion.div {...fadeInUp}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header
                  className="d-flex align-items-center gap-2"
                  style={{ padding: '1.25rem' }}
                >
                  <FaBuilding color="var(--pub-navy)" />
                  <h6 className="mb-0 fw-bold">Dane firmy</h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <Alert variant="light" className="border mb-4" style={{ borderRadius: '12px', color: '#495057', fontSize: '0.85rem' }}>
                    Te dane widzą Twoi połączeni pracownicy w swoim panelu, więc warto, żeby były aktualne.
                  </Alert>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Nazwa firmy</Form.Label>
                      <Form.Control
                        type="text" name="company_name" value={formData.company_name}
                        onChange={handleChange} required
                        style={{ borderRadius: '12px'}}
                      />
                    </Form.Group>

                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-medium">NIP</Form.Label>
                          <Form.Control
                            type="text" name="nip" value={formData.nip}
                            onChange={handleChange}
                            maxLength={10}
                            style={{ borderRadius: '12px'}}
                          />
                          <Form.Text className="text-muted">10 cyfr, bez myślników</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-medium">Telefon do firmy</Form.Label>
                          <Form.Control
                            type="tel" name="company_phone" value={formData.company_phone}
                            onChange={handleChange}
                            style={{ borderRadius: '12px'}}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-medium">Adres siedziby</Form.Label>
                          <Form.Control
                            type="text" name="company_address" value={formData.company_address}
                            onChange={handleChange}
                            style={{ borderRadius: '12px'}}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-medium">Miasto siedziby</Form.Label>
                          <Form.Control
                            type="text" name="company_city" value={formData.company_city}
                            onChange={handleChange}
                            style={{ borderRadius: '12px'}}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <GradientButton gradient="blue" type="submit" disabled={saving}>
                      <FaSave className="me-2" /> {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
                    </GradientButton>
                  </Form>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default EmployerSettings;